import { useEffect, useRef, useState } from 'react';

import { validatePayoutAddress, type PayoutValidation } from '../lib/api';

/**
 * Validating a Bitcoin payout address, once, for every screen that takes one.
 *
 * ===========================================================================
 * THE BROWSER CARRIES NO BITCOIN IMPLEMENTATION, AND MUST NOT.
 *
 * This asks the SERVER -- the same validator order creation uses. A second implementation
 * in the browser would have to reproduce BIP-173, BIP-350, base58check and their
 * checksums, and it would drift. Drift here has two outcomes and both are bad: a good
 * address refused, or, far worse, someone reassured about a bad one. Mined BTC is paid by
 * the network directly to whatever address is configured, and nobody can reverse it.
 *
 * ===========================================================================
 * WHY THIS IS A HOOK AND NOT THREE COPIES.
 *
 * Three screens now take a payout address: the configurator, the per-order editor, and
 * the account default. They must agree about every address, always. Extracted from
 * PayoutAddressField, which had the only implementation, so the behaviour here IS the
 * behaviour Configure already shipped: same debounce, same stale-response rule, same
 * reading of the server's answer.
 *
 * ===========================================================================
 * ADVISORY, NEVER AUTHORITATIVE.
 *
 * Every mutation revalidates server-side regardless. What this buys is that a customer
 * finds out before they submit, and that junk never reaches a route that changes where
 * money goes. `isSubmittable` is the guard callers use for that.
 */

/** How long after the last keystroke to ask. Unchanged from the configurator. */
const DEBOUNCE_MS = 350;

export type PayoutState =
  | { status: 'empty' }
  | { status: 'checking' }
  | { status: 'valid'; kind: string }
  | { status: 'invalid'; reason: string; powerpoolIdPending?: boolean };

/** Human names for the kinds the server reports. */
export const KIND_NAMES: Record<string, string> = {
  legacy: 'Legacy',
  segwit: 'SegWit',
  taproot: 'Taproot',
};

/**
 * Validate an address as it is typed.
 *
 * @param value The raw input. Trimmed here, so callers need not.
 * @returns the current state, and whether it is safe to submit.
 */
export function usePayoutAddressValidation(value: string): {
  state: PayoutState;
  /**
   * TRUE ONLY WHEN THE SERVER HAS SAID YES.
   *
   * Deliberately false while empty AND while checking -- an unresolved answer is not a
   * pass. This is what callers gate a submit on, so an address that has not been
   * validated cannot reach a route that moves a payout destination.
   */
  isSubmittable: boolean;
} {
  const [state, setState] = useState<PayoutState>({ status: 'empty' });

  /** Only the newest lookup may write state; a slow answer for an older value is dropped. */
  const latestRequestId = useRef(0);

  useEffect(() => {
    const trimmed = value.trim();

    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    if (trimmed.length === 0) {
      setState({ status: 'empty' });
      return undefined;
    }

    setState({ status: 'checking' });

    const controller = new AbortController();
    const timer = setTimeout(() => {
      validatePayoutAddress(trimmed, controller.signal)
        .then((result: PayoutValidation) => {
          if (requestId !== latestRequestId.current) return;

          setState(
            result.valid
              ? { status: 'valid', kind: result.kind ?? 'bitcoin' }
              : {
                  status: 'invalid',
                  reason: result.reason ?? 'That address cannot be used.',
                  powerpoolIdPending: result.powerpoolIdPending,
                },
          );
        })
        .catch(() => {
          if (requestId !== latestRequestId.current) return;
          if (controller.signal.aborted) return;

          // If the check itself fails we do NOT claim the address is fine. Failing closed
          // is the only safe direction for a field that decides where money goes.
          setState({ status: 'invalid', reason: 'Could not check that address right now.' });
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  return { state, isSubmittable: state.status === 'valid' };
}
